/**
 * International (non-US) Shipping Disclaimer
 *
 * Shows [data-eu-ship-banner] elements to everyone by default and hides them
 * ONLY when the visitor is confirmed to be in the US. Also gates the cart page
 * "Proceed to checkout" button (#checkout) behind the acknowledgement checkbox
 * while the notice is visible.
 *
 * Geolocation is layered, because Shopify's own signals can default to the
 * primary market (US) for a visitor it can't place — which would wrongly hide
 * the notice from international shoppers:
 *   1. A NON-US value from Shopify (server-side localization rendered into
 *      data-eu-country, or window.Shopify.country) is trusted immediately —
 *      Shopify never invents a random non-US country, so non-US is reliable.
 *   2. If Shopify says US or nothing, confirm against the real IP country
 *      (get.geojs.io, cached 12h) before hiding, so a mis-placed non-US visitor
 *      is still shown the notice.
 * Fail-open throughout: anything unknown, or any lookup failure, shows the
 * notice. Worst case a US shopper sees it; far better than a non-US shopper
 * missing the required warning.
 */
(function () {
  'use strict';

  var IP_CACHE_KEY = 'eu-ship-ip-country';
  var IP_CACHE_TTL = 12 * 60 * 60 * 1000; // 12 hours

  // Local (no network) country sources. A NON-US value here is trustworthy; a
  // 'US' or null value is treated as "unconfirmed" and verified against the IP.
  function localCountry() {
    try {
      var el = document.querySelector('[data-eu-ship-banner][data-eu-country]');
      var srv = el && el.getAttribute('data-eu-country');
      if (srv && srv.length === 2) return srv.toUpperCase();
    } catch (e) {}
    try {
      if (window.Shopify && typeof window.Shopify.country === 'string' && window.Shopify.country) {
        return window.Shopify.country.toUpperCase();
      }
    } catch (e) {}
    try {
      var lang = document.documentElement.getAttribute('lang') || '';
      var parts = lang.split('-');
      if (parts.length === 2 && parts[1].length === 2) return parts[1].toUpperCase();
    } catch (e) {}
    return null;
  }

  function cachedIpCountry() {
    try {
      var raw = localStorage.getItem(IP_CACHE_KEY);
      if (!raw) return undefined;
      var o = JSON.parse(raw);
      if (o && o.c && (Date.now() - o.t) < IP_CACHE_TTL) return o.c;
    } catch (e) {}
    return undefined;
  }

  // Real IP country via a free, CORS-friendly, no-key endpoint. Cached so it's
  // at most one request per browser per 12h. Calls back null on any failure.
  function fetchIpCountry(cb) {
    var cached = cachedIpCountry();
    if (cached !== undefined) { cb(cached); return; }
    var settled = false;
    var timer = setTimeout(function () { if (!settled) { settled = true; cb(null); } }, 2000);
    fetch('https://get.geojs.io/v1/ip/country.json')
      .then(function (r) { return r.json(); })
      .then(function (j) {
        if (settled) return; settled = true; clearTimeout(timer);
        var c = ((j && (j.country || j.country_code)) || '').toUpperCase();
        if (c) { try { localStorage.setItem(IP_CACHE_KEY, JSON.stringify({ c: c, t: Date.now() })); } catch (e) {} }
        cb(c || null);
      })
      .catch(function () { if (settled) return; settled = true; clearTimeout(timer); cb(null); });
  }

  function revealBanners() {
    document.querySelectorAll('[data-eu-ship-banner]').forEach(function (el) {
      el.removeAttribute('hidden');
    });
  }

  /* ── Checkout gating (cart page acknowledgement) ── */

  function ackRequired() {
    var ack = document.querySelector('[data-eu-ship-ack]');
    return ack && !ack.hasAttribute('hidden');
  }

  function ackChecked() {
    var box = document.querySelector('[data-eu-ship-ack-checkbox]');
    return !!box && box.checked;
  }

  function syncCheckoutGate() {
    var btn = document.getElementById('checkout');
    if (!btn) return;
    if (btn.dataset.euOrigDisabled === undefined) {
      btn.dataset.euOrigDisabled = btn.disabled ? 'true' : 'false';
    }
    if (ackRequired() && !ackChecked()) {
      btn.disabled = true;
      btn.dataset.euGated = 'true';
    } else if (btn.dataset.euGated === 'true') {
      btn.disabled = btn.dataset.euOrigDisabled === 'true';
      btn.dataset.euGated = 'false';
    }
  }

  function attachAckHandlers() {
    document.addEventListener('change', function (evt) {
      if (evt.target && evt.target.matches && evt.target.matches('[data-eu-ship-ack-checkbox]')) {
        syncCheckoutGate();
      }
    });

    // If the user manages to click the (disabled-styled) button area, nudge
    // attention to the acknowledgement card.
    document.addEventListener('click', function (evt) {
      var btn = document.getElementById('checkout');
      if (!btn || !ackRequired() || ackChecked()) return;
      var wrap = btn.closest('.cart__ctas');
      if (wrap && wrap.contains(evt.target)) {
        var ack = document.querySelector('[data-eu-ship-ack]');
        if (ack) {
          ack.classList.remove('eu-ship-ack--attention');
          void ack.offsetWidth;
          ack.classList.add('eu-ship-ack--attention');
          ack.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    });
  }

  var shown = false;
  function showNotice() {
    shown = true;
    revealBanners();
    syncCheckoutGate();
  }

  function decide() {
    var lc = localCountry();
    // Trusted non-US signal → show right away, no network call.
    if (lc && lc !== 'US') { showNotice(); return; }
    // Shopify says US or nothing — confirm against the real IP before hiding.
    fetchIpCountry(function (ipc) {
      if (ipc === 'US') return;  // confirmed US → keep hidden
      showNotice();              // non-US, or lookup failed → fail-open show
    });
  }

  function init() {
    attachAckHandlers();
    decide();

    // Cart re-renders can replace parts of the page — re-apply once we've
    // decided to show (never reveals for a confirmed-US visitor).
    ['cart:refresh', 'cart:updated', 'ajaxProduct:added', 'theme:cart:change'].forEach(function (ev) {
      document.addEventListener(ev, function () {
        if (shown) { revealBanners(); syncCheckoutGate(); }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();